import { useQuery } from '@tanstack/react-query';
import { contacts } from '@repletoryreach/sdk-api';
import { Badge, Card, Group, Skeleton, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { useT } from '../../../../i18n/I18nProvider';
import { formatDateTime, formatRelative } from '../../../../lib/format';

/**
 * The consent trail for this contact.
 *
 * `contacts-view-id.md` §4 item 6: the single opt-in badge was the whole
 * consent surface, on the page an operator opens to answer "when and how did
 * this person opt in?". The events have always been written to
 * `opt_in_events`; only a platform-admin route read them, so the tenant who
 * actually receives the POPIA request could not see their own record.
 * `GET /v1/contacts/{id}/consent` was added for this.
 */
export function ConsentTab({ contactId }: { contactId: string }) {
  const t = useT();
  const { data, isPending, error } = useQuery({
    queryKey: ['contact', contactId, 'consent'],
    queryFn: () => contacts.listConsentHistory(contactId),
    select: (r) => r.data ?? []
  });

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Title order={4}>{t('contacts.view.tabs.consent')}</Title>
        <Text fz="xs" c="var(--rr-color-text-muted)">{t('contacts.view.consent.hint')}</Text>

        {error && <Alert color="error" role="alert">{t('contacts.view.consent.error')}</Alert>}

        {isPending && <Stack gap="xs">{[0, 1, 2].map((i) => <Skeleton key={i} height={40} />)}</Stack>}

        {!isPending && !error && data!.length === 0 && (
          /* An empty trail is a real answer, not an error: contacts created
             before consent was recorded, or imported in bulk, have none. */
          <EmptyState
            title={t('contacts.view.consent.empty')}
            description={t('contacts.view.consent.empty.hint')}
          />
        )}

        {data?.map((e) => (
          <div key={e.id} className="rr-contact-view__consent">
            <Badge size="xs" variant="light" color={e.opted_in ? 'green' : 'red'}>
              {e.opted_in ? t('contacts.optIn.in') : t('contacts.optIn.out')}
            </Badge>
            <Stack gap={1} style={{ minWidth: 0 }}>
              <Text fz="sm">
                {t('contacts.view.consent.entry', {
                  channel: e.channel,
                  // `source` is a machine token ("api", "stop_keyword",
                  // "import"); translate it rather than showing the token.
                  source: t(`contacts.view.consent.source.${e.source ?? 'unknown'}`, e.source ?? '')
                })}
              </Text>
              {e.note && <Text fz="xs" c="var(--rr-color-text-muted)">{e.note}</Text>}
            </Stack>
            <Tooltip label={formatDateTime(e.occurred_at)} position="left">
              <span className="rr-contact-view__when">{formatRelative(e.occurred_at)}</span>
            </Tooltip>
          </div>
        ))}
      </Stack>
    </Card>
  );
}
